// Work's create-project flow: the in-flight guard, the capability probe, the
// create mutation and the first-turn hand-off, with in-place, promise-returning
// and navigating variants over one core. What is app-side crosses as
// `ProjectCreationPorts` (the desktop capability probe, the library preview the
// composer reset clears, the create mutation bound to the chat sidebar) and the
// route as `onNavigate`. The binding at `src/features/projects/useCreateAndOpenProject.ts`
// supplies both.
import type { NavigatePort } from '@heidi/runtime/shell';
import { getDefaultStore, type createStore } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  ClientCapabilities,
  CreateAndOpenProjectArgs,
  ProjectDetail,
} from '../../contracts';
import { openArtifactAtom } from './artifacts/state';
import type { useCreateProject } from './hooks';
import { pendingFirstTurnAtom, seededFirstTurnProjectIds } from './pendingFirstTurn';
import { resetProjectsComposerContextCore } from './resetProjectsComposerContext';
import { seedOptimisticFirstTurn } from './useOptimisticFirstTurn';

type JotaiStore = ReturnType<typeof createStore>;

/**
 * The app half of a create. Pass ONE module-level object: the create callback
 * lists it as a dependency, so a fresh object per render would re-create it.
 */
export interface ProjectCreationPorts {
  /** The create mutation, bound to the app's other lists that show projects. */
  useCreateProject: () => ReturnType<typeof useCreateProject>;
  /**
   * The desktop's screen capabilities for the create body, `null` on web. Also the
   * backend's desktop-attached signal; a rejection creates without them.
   */
  getClientCapabilities: () => Promise<ClientCapabilities | null>;
  /** Closes the app's library document preview, which the composer reset clears. */
  clearLibraryDocumentPreview: (store: JotaiStore) => void;
}

export interface UseCreateAndOpenProject {
  /** Create a project and hand off to its chat. Returns `false` when a create
   *  is already in flight (same guard as Session Work). */
  createAndOpenProject: (args: CreateAndOpenProjectArgs) => boolean;
  /** True for the whole create — synchronous click → optional capabilities
   *  probe → mutation → navigation — so the composer stays busy/disabled
   *  throughout. */
  isSubmitting: boolean;
}

/**
 * `seeded` — stashed in `pendingFirstTurnAtom` for the chat to dispatch once it
 * resolves the new id; `in-place` — the caller's mounted chat submits it, so
 * nothing is stashed and the composer context is left alone.
 */
type FirstTurnHandoff = 'seeded' | 'in-place';

export interface CreateProjectGuard {
  /** `false` when a create is already in flight. */
  acquire: () => boolean;
  release: () => void;
}

/** Shared by entry points that must never turn one intent into two projects. */
export const useCreateProjectGuard = (): CreateProjectGuard => {
  const busyRef = useRef(false);
  return useMemo(
    () => ({
      acquire: () => {
        if (busyRef.current) return false;
        busyRef.current = true;
        return true;
      },
      release: () => {
        busyRef.current = false;
      },
    }),
    []
  );
};

interface UseCreateProjectCoreArgs {
  /** Runs after the create succeeds and the first-turn hand-off is
   *  seeded. Owns whatever happens next (navigate, or point a panel at
   *  it) and must eventually call `releaseGuard` once that next step
   *  settles. */
  onCreated: (
    created: ProjectDetail,
    args: CreateAndOpenProjectArgs,
    releaseGuard: () => void
  ) => void;
  /** The create POST failed (guard already released). The create mutation has
   *  no feedback of its own — callers surface it here or the click reads as a
   *  silent no-op. */
  onCreateError?: (error: unknown) => void;
  firstTurnHandoff?: FirstTurnHandoff;
  guard?: CreateProjectGuard;
}

interface UseCreateProjectCore {
  /**
   * Start a create, or return `false` when one is already in flight (the atomic
   * guard drops it). Callers that record context against the pending create must
   * key on this so a dropped second call doesn't claim the first create's result.
   */
  create: (args: CreateAndOpenProjectArgs) => boolean;
  isSubmitting: boolean;
}

/**
 * Shared core behind both `useCreateAndOpenProject` (Work-home: navigates
 * to the project) and `useCreateSessionWorkProject` (session panel: points
 * the panel at it in place). Owns the in-flight guard, optional
 * screen-capabilities probe, create mutation, and first-turn hand-off seed
 * — everything up to "the project exists" — so the two entry points can
 * never drift on the create request shape.
 *
 * The guard is a synchronous ref (not just `createProject.isPending`)
 * because `isPending` only flips true after the mutation starts — and
 * computer-use flows can do an async capabilities probe before that. In
 * that window, a second submit or a composer remount would otherwise
 * start a duplicate create.
 */
export const useCreateProjectCore = (
  {
    onCreated,
    onCreateError,
    firstTurnHandoff = 'seeded',
    guard: sharedGuard,
  }: UseCreateProjectCoreArgs,
  ports: ProjectCreationPorts
): UseCreateProjectCore => {
  const { useCreateProject: useCreateMutation } = ports;
  const createProject = useCreateMutation();

  // The synchronous guard is the source of truth; `isCreating` only drives the UI.
  const ownGuard = useCreateProjectGuard();
  const guard = sharedGuard ?? ownGuard;
  const [isCreating, setIsCreating] = useState(false);
  const releaseGuard = useCallback(() => {
    guard.release();
    setIsCreating(false);
  }, [guard]);

  const create = useCallback(
    (args: CreateAndOpenProjectArgs) => {
      const {
        prompt,
        displayPrompt,
        title,
        files,
        linked,
        runFlags,
        computerUseSkill,
        source_template_id,
        source,
      } = args;
      if (!guard.acquire()) return false;
      setIsCreating(true);

      // Seed patient/session-scoped tools from the linked context (single-slot:
      // at most one session OR patient) so they resolve the subject without a
      // lookup.
      const linkedPatientId = linked?.type === 'patient' ? linked.patientId : undefined;
      const linkedSessionId =
        args.sessionIdOverride ??
        (linked?.type === 'session' ? linked.sessionId : undefined);

      const startCreateProject = (clientCapabilities?: ClientCapabilities | null) => {
        createProject.mutate(
          {
            title: title ?? 'New chat',
            tool_profile: 'clinician_desktop',
            ...(runFlags ? { run_flags: runFlags } : {}),
            ...(clientCapabilities ? { client_capabilities: clientCapabilities } : {}),
            ...(linkedPatientId ? { patient_profile_id: linkedPatientId } : {}),
            ...(linkedSessionId ? { session_id: linkedSessionId } : {}),
            ...(args.restricted ? { restricted: true } : {}),
            ...(source_template_id ? { source_template_id } : {}),
            ...(source ? { source } : {}),
          },
          {
            onSuccess: (created) => {
              if (firstTurnHandoff === 'seeded') {
                // Hand off the opening turn + staged files + linked context via
                // shared state (never the URL — PHI must not land in history /
                // logs). `ProjectChat` uploads the raw File objects against the
                // new project on the first turn. Write through the DEFAULT
                // jotai store so wherever `ProjectChat` mounts (routed page or
                // embedded panel) reads the same slot.
                getDefaultStore().set(pendingFirstTurnAtom, {
                  projectId: created.project_id,
                  prompt: prompt ?? '',
                  ...(displayPrompt && displayPrompt !== prompt ? { displayPrompt } : {}),
                  files: files ?? [],
                  ...(args.attachment_ids?.length
                    ? { attachment_ids: args.attachment_ids }
                    : {}),
                  ...(args.optimisticAttachments?.length
                    ? { optimisticAttachments: args.optimisticAttachments }
                    : {}),
                  computerUseSkill,
                  ...(args.desktopTargetOverride !== undefined
                    ? { desktopTargetOverride: args.desktopTargetOverride }
                    : {}),
                  ...(args.approvalMode ? { approvalMode: args.approvalMode } : {}),
                  ...(args.analytics ? { analytics: args.analytics } : {}),
                  ...(args.openingAssistantMessage
                    ? { openingAssistantMessage: args.openingAssistantMessage }
                    : {}),
                  linked: linked ?? null,
                  openArtifact: getDefaultStore().get(openArtifactAtom),
                });
                // Show the chat's working state (never the cold empty card)
                // through navigation/remount while the seeded turn lands.
                if (
                  (prompt ?? '').trim().length > 0 ||
                  computerUseSkill ||
                  (args.attachment_ids?.length ?? 0) > 0 ||
                  (files?.length ?? 0) > 0
                ) {
                  seededFirstTurnProjectIds.add(created.project_id);
                }
              }
              onCreated(created, args, releaseGuard);
            },
            // Create failed — release the guard so the user can retry.
            onError: (error) => {
              // In-place callers keep the canvas + chip: the turn never left the box.
              if (firstTurnHandoff === 'seeded') {
                resetProjectsComposerContextCore(ports.clearLibraryDocumentPreview);
              }
              releaseGuard();
              onCreateError?.(error);
            },
          }
        );
      };

      // Capabilities are best-effort and double as the backend's
      // desktop-attached signal. Desktop projects need them even for text-only
      // starts so the backend can expose `run_computer_use_task` when the model
      // later needs generic desktop control.
      void ports
        .getClientCapabilities()
        .catch(() => undefined)
        .then(startCreateProject);
      return true;
    },
    [
      createProject,
      firstTurnHandoff,
      guard,
      onCreated,
      onCreateError,
      ports,
      releaseGuard,
    ]
  );

  return { create, isSubmitting: isCreating };
};

export interface CreatedWorkChatProject {
  created: ProjectDetail;
  /** Until this is called the composer stays busy and cannot drain its queue. */
  releaseGuard: () => void;
}

export interface UseCreateWorkChatProject {
  /**
   * Resolves `null` when the in-flight guard drops the call, and REJECTS when
   * the create POST fails — the caller owns the retry and must never re-create.
   */
  createWorkChatProject: (
    args: CreateAndOpenProjectArgs
  ) => Promise<CreatedWorkChatProject | null>;
  isSubmitting: boolean;
}

/** No hand-off: nothing is stashed in `pendingFirstTurnAtom` and nothing
 *  navigates here — the caller owns both. */
export const useCreateWorkChatProject = (
  options: { guard?: CreateProjectGuard } | undefined,
  ports: ProjectCreationPorts
): UseCreateWorkChatProject => {
  const settleRef = useRef<{
    resolve: (value: CreatedWorkChatProject | null) => void;
    reject: (error: unknown) => void;
  } | null>(null);

  const settle = useCallback(
    (apply: (settler: NonNullable<typeof settleRef.current>) => void) => {
      const pending = settleRef.current;
      settleRef.current = null;
      if (pending) apply(pending);
    },
    []
  );

  const onCreated = useCallback(
    (
      created: ProjectDetail,
      _args: CreateAndOpenProjectArgs,
      releaseGuard: () => void
    ) => {
      settle((pending) => pending.resolve({ created, releaseGuard }));
    },
    [settle]
  );

  const onCreateError = useCallback(
    (error: unknown) => {
      settle((pending) => pending.reject(error));
    },
    [settle]
  );

  const { create, isSubmitting } = useCreateProjectCore(
    {
      onCreated,
      onCreateError,
      firstTurnHandoff: 'in-place',
      guard: options?.guard,
    },
    ports
  );

  const createWorkChatProject = useCallback(
    (args: CreateAndOpenProjectArgs) =>
      new Promise<CreatedWorkChatProject | null>((resolve, reject) => {
        // A create already owns the settler; taking the slot would strand it.
        if (settleRef.current) {
          resolve(null);
          return;
        }
        settleRef.current = { resolve, reject };
        // Latched before this returns, or a second send this tick starts a second
        // project.
        if (!create(args)) {
          settleRef.current = null;
          resolve(null);
        }
      }),
    [create]
  );

  return { createWorkChatProject, isSubmitting };
};

export interface UseCreateAndOpenProjectOptions {
  /** Surfaced when the create POST fails (guard already released). */
  onCreateError?: (error: unknown) => void;
  /** The project exists, before this hook navigates, so a host already showing the
   *  chat can adopt the id. Return `false` when the ordering context is gone: the
   *  hand-off is abandoned rather than navigating away from where the user moved. */
  onProjectCreated?: (projectId: string) => boolean | void;
  guard?: CreateProjectGuard;
  /** Wraps the post-create navigation so a host's own discard guard (e.g.
   *  `useQueuedTurnDiscardGuard`'s `runWithoutDiscardGuard`) recognizes this
   *  hand-off as an owned navigation rather than the user abandoning the chat.
   *  Defaults to an unwrapped call. */
  runNavigation?: <Result>(navigate: () => Promise<Result>) => Promise<Result>;
}

/**
 * Owns the Work-home "start a new project" flow: create via
 * {@link useCreateProjectCore}, then hand off to the project's own page through
 * `onNavigate`. Its identity feeds the create callback, so keep it stable.
 */
export const useCreateAndOpenProject = (
  options: UseCreateAndOpenProjectOptions & { onNavigate: NavigatePort },
  ports: ProjectCreationPorts
): UseCreateAndOpenProject => {
  const { onCreateError, onProjectCreated, onNavigate, runNavigation } = options;

  const onCreated = useCallback(
    (
      created: ProjectDetail,
      args: CreateAndOpenProjectArgs,
      releaseGuard: () => void
    ) => {
      if (onProjectCreated?.(created.project_id) === false) {
        releaseGuard();
        return;
      }
      // Remount-safe bubble for the project page: record before navigate so
      // `[id]` paints the user message immediately (Session Work pattern).
      seedOptimisticFirstTurn(
        created.project_id,
        args.displayPrompt ?? args.prompt ?? null,
        args.optimisticAttachments
      );
      resetProjectsComposerContextCore(ports.clearLibraryDocumentPreview);
      // Release however the navigation settles, or the composer wedges disabled.
      const navigate = () =>
        Promise.resolve(onNavigate(`/scribe/chat/projects/${created.project_id}`));
      void (runNavigation ? runNavigation(navigate) : navigate()).then(
        releaseGuard,
        releaseGuard
      );
    },
    [onNavigate, onProjectCreated, ports, runNavigation]
  );

  const { create, isSubmitting } = useCreateProjectCore(
    {
      onCreated,
      onCreateError,
      guard: options.guard,
    },
    ports
  );
  return { createAndOpenProject: create, isSubmitting };
};
